import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { anchorScroll } from '../../helpers/helperFunctions';
import { track } from '../../helpers/track';

// Photos de la session d'implantologie d'octobre 2026 (série Silviu, retouche
// 06/10/2026 : visages des patients recadrés ou floutés avant export).
import session from '../../assets/learning/session.webp'
import kit from '../../assets/learning/kit.webp'
import planning from '../../assets/learning/planning.webp'
import bloc from '../../assets/learning/bloc.webp'
import geste from '../../assets/learning/geste.webp'
import plateau from '../../assets/learning/plateau.webp'

import ContactForm from '../../components/ContactForm';

// Page « Formation » : Neomed forme régulièrement ses praticiens, au cabinet,
// en théorie puis en pratique. La dernière session (octobre 2026) était une
// journée d'implantologie avec le Dr Erwan Hauchard. Ton éditorial : factuel,
// pas de promesse chiffrée qu'on ne tient pas (fréquence, prise en charge).
function Learning(props) {
    const { isEnglish } = props

    useEffect(() => {
        // Même mécanique que la page « À propos » : révélation au scroll, une
        // seule fois par navigateur, null-safe (un sélecteur absent jetterait
        // et viderait la page).
        if (!localStorage.getItem('learning')) {
            const fadeInUp = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) entry.target.classList.add('fadeInUp');
                });
            });
            document.querySelectorAll('.learnReveal').forEach((el) => fadeInUp.observe(el));
            localStorage.setItem('learning', true)
        }
    }, [])

    const apply = (cta) => {
        track('learning_cta_click', { cta })
        anchorScroll('#contact-form')
    }

    const steps = isEnglish ? [
        {
            title: 'In the clinic, not in a conference hall',
            text: 'Sessions take place in our clinics in Châteauroux, Déols and Saint-Maur, with the equipment our practitioners use every day. What is learned on the day can be applied the next morning, on the same chair, with the same team.',
        },
        {
            title: 'Theory built on your own cases',
            text: 'The trainer works from the practitioners\' files: indications, reading the imaging, planning the placement, choosing the implant system, drilling sequence. The surgical kits are on the table, not only on the slides.',
        },
        {
            title: 'Practice, assisted',
            text: 'Practitioners then operate in the surgery on patients of the clinic, with the trainer at their side throughout the procedure. The dental assistants take part too: a protocol only holds if the whole team knows it.',
        },
    ] : [
        {
            title: 'Au cabinet, pas en salle de congrès',
            text: 'Les sessions ont lieu dans nos cabinets de Châteauroux, Déols et Saint-Maur, avec le matériel que nos praticiens utilisent au quotidien. Ce qui est appris le jour même est réutilisable dès le lendemain, au même fauteuil, avec la même équipe.',
        },
        {
            title: 'La théorie à partir de vos propres cas',
            text: 'Le formateur travaille sur les dossiers des praticiens : indications, lecture de l\'imagerie, planification de la pose, choix du système implantaire, séquence de forage. Les kits chirurgicaux sont sur la table, pas seulement sur les diapositives.',
        },
        {
            title: 'La pratique, assistée',
            text: 'Les praticiens opèrent ensuite au bloc, sur des patients du cabinet, le formateur à leurs côtés pendant toute l\'intervention. Les assistantes dentaires y participent aussi : un protocole ne tient que si toute l\'équipe le connaît.',
        },
    ]

    const gallery = [
        { src: kit, alt: isEnglish ? 'A practitioner handles the implant kit, the trainer leaning in' : 'Une praticienne manipule le kit implantaire, le formateur penché à ses côtés', caption: isEnglish ? 'Hands on the implant kit, during the theory session' : 'Le kit implantaire en main, pendant la partie théorique' },
        { src: planning, alt: isEnglish ? 'The trainer and a practitioner plan a case together on the laptop' : 'Le formateur et un praticien planifient un cas ensemble sur l\'ordinateur', caption: isEnglish ? 'Planning the case, side by side' : 'Planification du cas, à deux' },
        { src: bloc, alt: isEnglish ? 'The team around the patient during an assisted implant placement' : 'L\'équipe autour du patient pendant une pose d\'implant assistée', caption: isEnglish ? 'In the surgery: assisted placement, the team around' : 'Au bloc : pose assistée, l\'équipe autour' },
        { src: geste, alt: isEnglish ? 'The surgical procedure under the operating light' : 'Le geste chirurgical sous le scialytique', caption: isEnglish ? 'The procedure, under the surgical light' : 'Le geste, sous le scialytique' },
    ]

    return (
        <div className='overflow-x-hidden'>
            {/* Héros */}
            <div className='mt-28 sm:mt-32 overflow-hidden bg-white'>
                <div className='mx-auto max-w-7xl px-6 sm:px-12 lg:grid lg:grid-cols-12 lg:gap-x-10 lg:items-center'>
                    <div className='lg:col-span-6'>
                        <span className="inline-flex items-center rounded-full bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-700/10 mb-4">
                            {isEnglish ? 'CONTINUING EDUCATION' : 'FORMATION CONTINUE'}
                        </span>
                        <h1 className='text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900'>
                            {isEnglish ? 'Regular training, in the clinic, in theory and in practice' : 'Des formations régulières, au cabinet, en théorie et en pratique'}
                        </h1>
                        <p className='mt-6 text-sm sm:text-lg leading-7 text-gray-600'>
                            {isEnglish
                                ? 'Neomed regularly organises training sessions for its practitioners, directly in our clinics. A recognised trainer spends the day with the team: a theory part built on the practitioners\' own cases, then a practical part in the surgery, on real patients.'
                                : 'Neomed organise régulièrement des sessions de formation pour ses praticiens, directement dans nos cabinets. Un formateur reconnu vient passer la journée avec l\'équipe : une partie théorique autour des cas des praticiens, puis une partie pratique au bloc, sur de vrais patients.'}
                        </p>
                        <p className='mt-3 sm:mt-4 text-sm sm:text-lg leading-7 text-gray-600'>
                            {isEnglish
                                ? 'The aim is simple: a dentist who joins Neomed keeps progressing in the disciplines they practised little, or not at all, before arriving, implantology first, and does so with their colleagues, in their own working environment.'
                                : 'L\'objectif est simple : qu\'un dentiste qui rejoint Neomed continue à progresser dans les disciplines qu\'il pratiquait peu, ou pas, avant son arrivée, l\'implantologie en premier lieu, et qu\'il le fasse avec ses collègues, dans son propre environnement de travail.'}
                        </p>
                        <div className='mt-6 flex flex-wrap gap-3'>
                            <button
                                onClick={() => apply('hero')}
                                className='inline-flex items-center rounded-xl bg-gray-900 px-5 py-3 text-sm sm:text-md font-semibold text-white shadow-sm cursor-pointer transition-all duration-200 hover:bg-brand-600 hover:scale-[1.02]'
                            >
                                {isEnglish ? 'Join the team' : 'Rejoindre l\'équipe'}<span className='ml-2' aria-hidden='true'>&rarr;</span>
                            </button>
                            <Link
                                to='/careers'
                                onClick={() => props.setCurrentScreen && props.setCurrentScreen('/careers')}
                                className='inline-flex items-center rounded-xl border border-gray-300 px-5 py-3 text-sm sm:text-md font-semibold text-gray-900 transition-all duration-200 hover:border-gray-900'
                            >
                                {isEnglish ? 'Open positions' : 'Postes ouverts'}
                            </Link>
                        </div>
                    </div>
                    <div className='mt-10 lg:mt-0 lg:col-span-6'>
                        <img
                            src={session}
                            alt={isEnglish ? 'Training session: the trainer standing, the practitioners around the table' : 'Session de formation : le formateur debout, les praticiens autour de la table'}
                            className='w-full aspect-[3/2] rounded-2xl bg-gray-50 object-cover shadow-lg'
                        />
                    </div>
                </div>
            </div>

            {/* Comment nous formons */}
            <div className='mx-auto max-w-7xl px-6 sm:px-12 mt-16 sm:mt-24'>
                <h2 className='text-xl sm:text-2xl font-bold tracking-tight text-gray-900'>
                    {isEnglish ? 'How our sessions work' : 'Comment se déroulent nos sessions'}
                </h2>
                <div className='mt-8 grid grid-cols-1 gap-6 md:grid-cols-3'>
                    {steps.map((step, i) => (
                        <div key={step.title} className='learnReveal rounded-2xl border border-gray-100 bg-white p-6 sm:p-8 shadow-md'>
                            <span className='text-sm font-semibold text-brand-700'>{String(i + 1).padStart(2, '0')}</span>
                            <h3 className='mt-2 text-lg sm:text-xl font-semibold text-gray-900'>{step.title}</h3>
                            <p className='mt-3 text-sm sm:text-base leading-7 text-gray-600'>{step.text}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Dernière session */}
            <div className='mx-auto max-w-7xl px-6 sm:px-12 mt-16 sm:mt-24'>
                <div className='rounded-2xl bg-gray-50 border border-gray-100 p-6 sm:p-10 lg:grid lg:grid-cols-12 lg:gap-x-10'>
                    <div className='lg:col-span-7'>
                        <span className="inline-flex items-center rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 ring-1 ring-inset ring-gray-900/10 mb-4">
                            {isEnglish ? 'LATEST SESSION · OCTOBER 2026' : 'DERNIÈRE SESSION · OCTOBRE 2026'}
                        </span>
                        <h2 className='text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-gray-900'>
                            {isEnglish ? 'Implantology with Dr Erwan Hauchard' : 'Implantologie avec le Dr Erwan Hauchard'}
                        </h2>
                        <p className='mt-4 text-sm sm:text-lg leading-7 text-gray-600'>
                            {isEnglish
                                ? 'Dr Erwan Hauchard is a recognised implantologist who teaches at Université Paris Cité. He came to train our dentists in theory and then in practice: assisted implant placement, on the clinic\'s own patients.'
                                : 'Le Dr Erwan Hauchard est un implantologue reconnu, qui enseigne à l\'Université Paris Cité. Il est venu former nos dentistes en théorie puis en pratique : pose d\'implants assistée, sur les patients du cabinet.'}
                        </p>
                        <dl className='mt-6 space-y-5'>
                            <div>
                                <dt className='text-base font-semibold text-gray-900'>{isEnglish ? 'Theory' : 'Théorie'}</dt>
                                <dd className='mt-1 text-sm sm:text-base leading-7 text-gray-600'>
                                    {isEnglish
                                        ? 'Indications and contraindications, reading the imaging, planning the placement, choosing the implant system, getting to grips with the surgical kit and its drilling sequence.'
                                        : 'Indications et contre-indications, lecture de l\'imagerie, planification de la pose, choix du système implantaire, prise en main du kit chirurgical et de sa séquence de forage.'}
                                </dd>
                            </div>
                            <div>
                                <dt className='text-base font-semibold text-gray-900'>{isEnglish ? 'Practice' : 'Pratique'}</dt>
                                <dd className='mt-1 text-sm sm:text-base leading-7 text-gray-600'>
                                    {isEnglish
                                        ? 'Assisted implant placements in the surgery: the practitioner operates, Dr Hauchard at their side, with the dental assistant and the clinic team observing each step.'
                                        : 'Poses d\'implants assistées au bloc : le praticien opère, le Dr Hauchard à ses côtés, avec l\'assistante et l\'équipe du cabinet qui suivent chaque étape.'}
                                </dd>
                            </div>
                        </dl>
                    </div>
                    <div className='mt-8 lg:mt-0 lg:col-span-5 flex items-center'>
                        <img
                            src={plateau}
                            alt={isEnglish ? 'The surgical tray: implant kit and gloved hands' : 'Le plateau chirurgical : kit implantaire et mains gantées'}
                            className='w-full max-h-[32rem] rounded-2xl object-cover shadow-lg'
                        />
                    </div>
                </div>

                <div className='mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2'>
                    {gallery.map((photo) => (
                        <figure key={photo.src} className='learnReveal'>
                            <img src={photo.src} alt={photo.alt} className='w-full aspect-[3/2] rounded-2xl bg-gray-50 object-cover shadow-md' loading='lazy' />
                            <figcaption className='mt-2 text-sm text-gray-500'>{photo.caption}</figcaption>
                        </figure>
                    ))}
                </div>
            </div>

            {/* Pour qui, et ensuite */}
            <div className='mx-auto max-w-7xl px-6 sm:px-12 mt-16 sm:mt-24 pb-16 sm:pb-24'>
                <div className='lg:grid lg:grid-cols-12 lg:gap-x-10'>
                    <div className='lg:col-span-7'>
                        <h2 className='text-xl sm:text-2xl font-bold tracking-tight text-gray-900'>
                            {isEnglish ? 'For every practitioner in our clinics' : 'Pour tous les praticiens de nos cabinets'}
                        </h2>
                        <p className='mt-4 text-sm sm:text-lg leading-7 text-gray-600'>
                            {isEnglish
                                ? 'These sessions are open to all the dentists of our clinics, whatever their seniority. For a practitioner trained abroad, they are also a way to learn protocols as they are practised in France, in a setting where every question can be asked.'
                                : 'Ces sessions sont ouvertes à l\'ensemble des dentistes de nos cabinets, quelle que soit leur ancienneté. Pour un praticien formé à l\'étranger, c\'est aussi une façon d\'apprendre les protocoles tels qu\'ils se pratiquent en France, dans un cadre où l\'on peut poser toutes ses questions.'}
                        </p>
                        <p className='mt-3 sm:mt-4 text-sm sm:text-lg leading-7 text-gray-600'>
                            {isEnglish
                                ? 'Other sessions will follow, in implantology and in the other disciplines practised in our clinics.'
                                : 'D\'autres sessions suivront, en implantologie comme dans les autres disciplines pratiquées dans nos cabinets.'}
                        </p>
                    </div>
                    <div className='mt-8 lg:mt-0 lg:col-span-5'>
                        <div className='rounded-2xl bg-gray-900 p-6 sm:p-8 text-white'>
                            <h3 className='text-lg sm:text-xl font-semibold'>
                                {isEnglish ? 'Practise where training is part of the job' : 'Exercer là où la formation fait partie du métier'}
                            </h3>
                            <p className='mt-3 text-sm sm:text-base leading-7 text-white/80'>
                                {isEnglish
                                    ? 'Dentist and orthodontist positions in Châteauroux, Déols and Saint-Maur, with a full schedule from day one and a dedicated assistant.'
                                    : 'Postes de dentiste et d\'orthodontiste à Châteauroux, Déols et Saint-Maur, avec un planning rempli dès le premier jour et une assistante dédiée.'}
                            </p>
                            <div className='mt-6 flex flex-wrap gap-3'>
                                <button
                                    onClick={() => apply('footer')}
                                    className='inline-flex items-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-gray-900 shadow-sm transition-all duration-200 hover:bg-brand-50 hover:scale-[1.02]'
                                >
                                    {isEnglish ? 'Apply' : 'Postuler'}<span className='ml-2' aria-hidden='true'>&rarr;</span>
                                </button>
                                <Link
                                    to='/careers'
                                    onClick={() => props.setCurrentScreen && props.setCurrentScreen('/careers')}
                                    className='inline-flex items-center rounded-xl border border-white/40 px-5 py-3 text-sm font-semibold text-white transition-all duration-200 hover:border-white'
                                >
                                    {isEnglish ? 'Salaries & positions' : 'Salaires & postes'}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='relative isolate'>
                <ContactForm isEnglish={isEnglish} />
            </div>
        </div>
    )
}

export default Learning
